import React, { useState, useCallback } from 'react';
import apiFetch from '../../services/apiFetch';
import './FactsPanel.css';


export default function FactsPanel({ card, token, onFactsChange }) {
    const [collapsed, setCollapsed] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    const facts = card?.facts || [];

    const handleAdd = useCallback(async (data) => {
        const res = await apiFetch(`/api/cards/${card.id}/facts`, token, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        if (res.ok) {
            const updated = await res.json();
            onFactsChange(updated);
            setShowAddForm(false);
        }
    }, [card?.id, token, onFactsChange]);

    const handleUpdate = useCallback(async (factId, data) => {
        const res = await apiFetch(`/api/cards/${card.id}/facts/${factId}`, token, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        if (res.ok) {
            const updated = await res.json();
            onFactsChange(updated);
            setEditingId(null);
        }
    }, [card?.id, token, onFactsChange]);

    const handleDelete = useCallback(async (factId) => {
        if (!window.confirm('Delete this fact?')) return;
        const res = await apiFetch(`/api/cards/${card.id}/facts/${factId}`, token, {
            method: 'DELETE',
        });
        if (res.ok) {
            const updated = await res.json();
            onFactsChange(updated);
        }
    }, [card?.id, token, onFactsChange]);

    if (!card) return null;

    return (
        <div className="fp-panel">
            <button className="fp-toggle" onClick={() => setCollapsed(!collapsed)}>
                <div className="fp-toggle-left">
                    <i className={`pi ${collapsed ? 'pi-chevron-right' : 'pi-chevron-down'}`} />
                    <span className="fp-toggle-title">Sources & Facts</span>
                    {facts.length > 0 && <span className="fp-count">{facts.length}</span>}
                </div>
            </button>

            {!collapsed && (
                <div className="fp-content">
                    {facts.length === 0 && !showAddForm && (
                        <p className="fp-empty">No facts saved for this card yet.</p>
                    )}

                    {facts.map(fact => (
                        editingId === fact.id ? (
                            <FactForm
                                key={fact.id}
                                initial={fact}
                                onSave={(data) => handleUpdate(fact.id, data)}
                                onCancel={() => setEditingId(null)}
                            />
                        ) : (
                            <div key={fact.id} className="fp-fact">
                                <p className="fp-fact-text">{fact.text}</p>
                                <div className="fp-fact-meta">
                                    {fact.source_url && (
                                        <a
                                            href={fact.source_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="fp-fact-link"
                                        >
                                            <i className="pi pi-external-link" />
                                            {fact.source_title || 'Source'}
                                        </a>
                                    )}
                                    <div className="fp-fact-actions">
                                        <button
                                            className="fp-icon-btn"
                                            onClick={() => setEditingId(fact.id)}
                                            title="Edit"
                                        >
                                            <i className="pi pi-pencil" />
                                        </button>
                                        <button
                                            className="fp-icon-btn fp-icon-btn-danger"
                                            onClick={() => handleDelete(fact.id)}
                                            title="Delete"
                                        >
                                            <i className="pi pi-trash" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    ))}

                    {showAddForm ? (
                        <FactForm
                            onSave={handleAdd}
                            onCancel={() => setShowAddForm(false)}
                        />
                    ) : (
                        <button className="fp-add-btn" onClick={() => setShowAddForm(true)}>
                            <i className="pi pi-plus" /> Add fact
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function FactForm({ initial, onSave, onCancel }) {
    const [text, setText] = useState(initial?.text || '');
    const [sourceUrl, setSourceUrl] = useState(initial?.source_url || '');
    const [sourceTitle, setSourceTitle] = useState(initial?.source_title || '');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async () => {
        if (!text.trim()) return;
        setSaving(true);
        await onSave({ text, source_url: sourceUrl, source_title: sourceTitle });
        setSaving(false);
    };

    return (
        <div className="fp-form">
            <label className="fp-form-label">Fact text</label>
            <textarea
                className="fp-form-input fp-form-textarea"
                value={text}
                onChange={e => setText(e.target.value)}
                rows={2}
            />
            <label className="fp-form-label">Source URL</label>
            <input
                className="fp-form-input"
                value={sourceUrl}
                onChange={e => setSourceUrl(e.target.value)}
            />
            <label className="fp-form-label">Source title</label>
            <input
                className="fp-form-input"
                value={sourceTitle}
                onChange={e => setSourceTitle(e.target.value)}
                placeholder="e.g. Wikipedia, Official Report..."
            />
            <div className="fp-form-actions">
                <button className="ne-btn" onClick={onCancel} disabled={saving}>Cancel</button>
                <button className="ne-btn ne-btn-primary" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
}
